import {Flag, type FlagProps} from 'platform/components';
import {Box, HStack, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {type CampaignStatusValue} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

type SalesCampaignStatusFlagAppearance = Pick<FlagProps, 'label' | 'colorScheme'> & {
  hasDot: boolean;
};

function getSalesCampaignStatusFlagAppearance(
  status: CampaignStatusValue
): SalesCampaignStatusFlagAppearance {
  return match<CampaignStatusValue, SalesCampaignStatusFlagAppearance>(status)
    .with('SCHEDULED', () => ({
      label: i18n.t('entity.salesCampaign.status.scheduled'),
      colorScheme: 'purple',
      hasDot: false,
    }))
    .with('ACTIVE', () => ({
      label: i18n.t('entity.salesCampaign.status.active'),
      colorScheme: 'green',
      hasDot: true,
    }))
    .with('ENDED', () => ({
      label: i18n.t('entity.salesCampaign.status.ended'),
      colorScheme: 'neutral',
      hasDot: false,
    }))
    .exhaustive();
}

interface SalesCampaignStatusFlagProps extends RequiredTestIdProps {
  status: CampaignStatusValue;
}

export function SalesCampaignStatusFlag(props: SalesCampaignStatusFlagProps) {
  const {label, colorScheme, hasDot} = getSalesCampaignStatusFlagAppearance(props.status);

  return (
    <HStack spacing={1} align="center">
      <Show when={hasDot}>
        <Box
          width={2}
          height={2}
          borderRadius="circular"
          backgroundColor="palettes.green.60.100"
          data-testid={suffixTestId('statusDot', props)}
        />
      </Show>
      <Flag
        label={label}
        colorScheme={colorScheme}
        isSubtle
        size="default"
        data-testid={props['data-testid']}
      />
    </HStack>
  );
}
